import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../../api/client';
import { getVideoCategory, VIDEO_CATEGORIES, VIDEO_TYPE_LABELS } from '../../utils/videoCategories';

const PAGE_SIZE = 40;

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'video', label: 'Videos' },
  { id: 'stream', label: 'Live streams' },
  { id: 'short', label: 'Shorts' },
  { id: 'featured', label: 'Featured' },
  { id: 'hidden', label: 'Hidden' },
];

function metaPayload(video, patch = {}) {
  return {
    typeOverride: video.typeOverride,
    category: video.category,
    featured: video.featured,
    hidden: video.hidden,
    ...patch,
  };
}

export default function AdminVideos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [message, setMessage] = useState('');
  const [syncing, setSyncing] = useState(false);

  const load = () => apiGet('/videos?all=1').then(setVideos).finally(() => setLoading(false));

  useEffect(() => {
    load().catch((err) => setMessage(err.message));
  }, []);

  const counts = useMemo(() => ({
    all: videos.length,
    video: videos.filter((v) => v.type === 'video').length,
    stream: videos.filter((v) => v.type === 'stream').length,
    short: videos.filter((v) => v.type === 'short').length,
    featured: videos.filter((v) => v.featured).length,
    hidden: videos.filter((v) => v.hidden).length,
  }), [videos]);

  const query = search.trim().toLowerCase();
  const filtered = videos.filter((v) => {
    if (filter === 'featured' && !v.featured) return false;
    if (filter === 'hidden' && !v.hidden) return false;
    if (['video', 'stream', 'short'].includes(filter) && v.type !== filter) return false;
    return !query || v.snippet.title.toLowerCase().includes(query);
  });

  const save = async (video, patch) => {
    const payload = metaPayload(video, patch);
    const resolvedType = payload.typeOverride || video.detectedType;
    setVideos((list) => list.map((v) => {
      if (v.id.videoId === video.id.videoId) {
        return { ...v, ...payload, type: resolvedType };
      }
      // Only one video is featured on the home page at a time.
      return patch.featured ? { ...v, featured: false } : v;
    }));
    try {
      if (patch.featured) {
        await Promise.all(videos
          .filter((v) => v.featured && v.id.videoId !== video.id.videoId)
          .map((v) => apiPut(`/videos/${v.id.videoId}`, metaPayload(v, { featured: false }))));
      }
      await apiPut(`/videos/${video.id.videoId}`, payload);
      setMessage(`Saved “${video.snippet.title}”.`);
    } catch (err) {
      setMessage(err.message);
      load();
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    setMessage('');
    try {
      const result = await apiPost('/videos/sync');
      setMessage(`Synced ${result.count} videos from YouTube. Types were re-detected; your overrides were kept.`);
      load();
    } catch (err) {
      setMessage(`Sync failed: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="admin-panel-page">
      <div className="admin-panel-toolbar">
        <div className="admin-panel-toolbar-row">
          <div className="admin-stat-chips">
            <div className="admin-stat-chip">
              <span className="admin-stat-value">{counts.video}</span>
              <span className="admin-stat-label">Videos</span>
            </div>
            <div className="admin-stat-chip admin-stat-chip--purple">
              <span className="admin-stat-value">{counts.stream}</span>
              <span className="admin-stat-label">Streams</span>
            </div>
            <div className="admin-stat-chip admin-stat-chip--gold">
              <span className="admin-stat-value">{counts.featured}</span>
              <span className="admin-stat-label">Featured</span>
            </div>
            <div className="admin-stat-chip">
              <span className="admin-stat-value">{counts.hidden}</span>
              <span className="admin-stat-label">Hidden</span>
            </div>
          </div>
          <div className="admin-panel-toolbar-actions">
            <a href="/videos" target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
              <i className="fas fa-external-link-alt" aria-hidden="true" /> View page
            </a>
            <button type="button" className="btn btn-primary" onClick={syncNow} disabled={syncing}>
              <i className={`fas fa-sync${syncing ? ' fa-spin' : ''}`} aria-hidden="true" /> {syncing ? 'Syncing…' : 'Sync from YouTube'}
            </button>
          </div>
        </div>
        <div className="admin-filter-row">
          <div className="admin-search">
            <i className="fas fa-search" aria-hidden="true" />
            <input value={search} onChange={(e) => { setSearch(e.target.value); setVisibleCount(PAGE_SIZE); }} placeholder="Search video titles…" />
          </div>
          <div className="admin-panel-tabs">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`admin-panel-tab ${filter === f.id ? 'active' : ''}`}
                onClick={() => { setFilter(f.id); setVisibleCount(PAGE_SIZE); }}
              >
                {f.label} <span className="admin-tab-count">{counts[f.id]}</span>
              </button>
            ))}
          </div>
          <span className="admin-filter-hint">Changes save instantly · ★ puts a video in the home page spotlight</span>
        </div>
      </div>

      {message && <p className="admin-message">{message}</p>}

      <div className="admin-list-wrap">
        {loading ? (
          <div className="admin-list-empty"><p>Loading videos…</p></div>
        ) : filtered.length === 0 ? (
          <div className="admin-list-empty"><p>No videos match this filter.</p></div>
        ) : (
          <ul className="admin-list">
            {filtered.slice(0, visibleCount).map((v) => {
              const autoCategory = getVideoCategory({ ...v, category: null });
              return (
                <li className={`admin-list-item admin-video-row${v.hidden ? ' is-draft' : ''}`} key={v.id.videoId}>
                  <img src={v.snippet.thumbnails.high.url} alt="" className="admin-thumb admin-thumb--wide" loading="lazy" />
                  <div className="admin-list-main">
                    <div className="admin-list-title-row">
                      <a
                        className="admin-list-title"
                        href={`https://www.youtube.com/watch?v=${v.id.videoId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {v.snippet.title}
                      </a>
                    </div>
                    <div className="admin-list-pills">
                      <span className="admin-pill is-muted">{new Date(v.snippet.publishedAt).toLocaleDateString()}</span>
                      {v.durationSeconds != null && (
                        <span className="admin-pill is-muted">
                          {Math.floor(v.durationSeconds / 60)}:{String(v.durationSeconds % 60).padStart(2, '0')}
                        </span>
                      )}
                      {v.hidden && <span className="admin-pill is-muted"><i className="fas fa-eye-slash" aria-hidden="true" /> Hidden</span>}
                    </div>
                    <div className="admin-video-controls">
                      <label>
                        <span>Type</span>
                        <select
                          value={v.typeOverride || ''}
                          onChange={(e) => save(v, { typeOverride: e.target.value || null })}
                        >
                          <option value="">Auto ({VIDEO_TYPE_LABELS[v.detectedType] || v.detectedType})</option>
                          {Object.entries(VIDEO_TYPE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                      </label>
                      <label>
                        <span>Category</span>
                        <select
                          value={v.category || ''}
                          onChange={(e) => save(v, { category: e.target.value || null })}
                        >
                          <option value="">Auto ({autoCategory})</option>
                          {VIDEO_CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                        </select>
                      </label>
                    </div>
                  </div>
                  <div className="admin-list-actions">
                    <button
                      type="button"
                      className={`admin-icon-btn ${v.featured ? 'is-active' : ''}`}
                      onClick={() => save(v, { featured: !v.featured })}
                      title={v.featured ? 'Remove from home page spotlight' : 'Spotlight on the home page'}
                    >
                      <i className={`${v.featured ? 'fas' : 'far'} fa-star`} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className={`admin-icon-btn ${v.hidden ? 'danger' : ''}`}
                      onClick={() => save(v, { hidden: !v.hidden })}
                      title={v.hidden ? 'Show on the site' : 'Hide from the site'}
                    >
                      <i className={`fas ${v.hidden ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {filtered.length > visibleCount && (
          <div className="admin-list-more">
            <button type="button" className="btn btn-secondary" onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}>
              Show more ({filtered.length - visibleCount} left)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
